import React, {useEffect, useRef, useState} from 'react';
import {Box, Divider, Typography} from '@mui/material';
import {t} from 'i18next';

import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../src/assets/images/info-icon.svg';
import {ArrToggle} from '../../common/arr-toggle/ArrToggle';
import {useDispatch, useSelector} from 'react-redux';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrBrandingLocation from '../../common/arr-branding-location/ArrBrandingLocation';
import {DropDownMenuOptions, LOCATION_MAP} from './BrandingAndRedactionsUtils';
import {ISelectItem} from '../../../shared/types';
import './BrandingAndRedactions.scss';
import {
  setBrandingDetails,
  setIsIncludeRedactions,
  setShouldBurnTransparentRedactions,
} from '../../../redux/slice/batchPrintSlice';
import ArrRichTextEditor from '../../common/arr-rich-text-editor/ArrRichTextEditor';

export type BrandingKey =
  | 'top-Left'
  | 'top-Center'
  | 'top-Right'
  | 'bottom-Left'
  | 'bottom-Center'
  | 'bottom-Right';

export type BrandingLocationDetails = {
  Location: number;
  Html: string;
};

const BrandingAndRedactions = () => {
  const dispatch = useDispatch();
  const batchPrint = useSelector((state: any) => state.batchPrint);
  const brandingPayload: BrandingLocationDetails[] =
    batchPrint.brandingDetails ?? [];
  const disabledBrandingKeys: BrandingKey[] = [];
  const dataTestId = 'arr-filter-tooltip';

  const locationSectionRef = useRef<HTMLDivElement | null>(null);

  const titleInfo = () => (
    <>
      <p data-testid={`branding-and-redactions-tt0`}>
        {t('batchPrint.brandingAndRedactions.titleTooltip.tt0')}
      </p>
      <p data-testid={`branding-and-redactions-tt1`}>
        {t('batchPrint.brandingAndRedactions.titleTooltip.tt1')}
      </p>
      <p data-testid={`branding-and-redactions-tt2`}>
        {t('batchPrint.brandingAndRedactions.titleTooltip.tt2')}
      </p>
    </>
  );

  const brandingLocationInfo = () => (
    <>
      <p data-testid={`branding-and-redactions-tt0-text`}>
        {t('batchPrint.brandingAndRedactions.brandingLocationTooltip.tt0')}
      </p>
      <p data-testid={`branding-and-redactions-tt1-text`}>
        {t('batchPrint.brandingAndRedactions.brandingLocationTooltip.tt1')}
      </p>
    </>
  );

  if (batchPrint.saveBatchPrint?.batchPrintDTO?.includeTableOfContent) {
    disabledBrandingKeys.push('bottom-Right');
  }

  const dropdownSelection =
    DropDownMenuOptions.find(
      (opt) => opt.value === String(batchPrint.shouldBurnTransparentRedactions)
    ) || DropDownMenuOptions[0];

  const [activeKey, setActiveKey] = useState<BrandingKey | null>(null);
  const defaultBrandingRef = useRef<HTMLHeadingElement | null>(null);
  const noteTextRef = useRef<HTMLParagraphElement | null>(null);

  const handleLocationChange = (key: BrandingKey) => {
    setActiveKey(key);
  };

  const handleReset = () => {
    setActiveKey(null);
    dispatch(setBrandingDetails([]));
  };

  const handleDropdownChange = (selected: ISelectItem | null) => {
    dispatch(setShouldBurnTransparentRedactions(selected?.value === 'true'));
  };

  const filledKeys: BrandingKey[] = brandingPayload
    ?.map((item) =>
      Object.keys(LOCATION_MAP).find(
        (key) => LOCATION_MAP[key as BrandingKey] === item.Location
      )
    )
    .filter(Boolean) as BrandingKey[];

  useEffect(() => {
    if (activeKey && disabledBrandingKeys.includes(activeKey)) {
      setActiveKey(null);
      return;
    }
  }, [activeKey, disabledBrandingKeys]);

  useEffect(() => {
    if (activeKey) {
      defaultBrandingRef.current?.focus();
    }
  }, [activeKey]);

  return (
    <Box
      className="branding-and-redactions-container"
      data-testid="branding-and-redactions-container"
    >
      <Box className="bar-header" data-testid="branding-and-redactions-header">
        <Typography
          className="typo-text subhead2_size"
          tabIndex={0}
          data-testid={`branding-and-redactions-title`}
        >
          {t('batchPrint.brandingAndRedactions.title')}
        </Typography>
        <ArrTooltipWrapper
          title={t('batchPrint.brandingAndRedactions.title')}
          dataTestId="branding-main-title-tooltip"
          trigger={
            <InfoIcon
              fontSize="small"
              className="info-icon"
              data-testid={`add-user-title-info-${dataTestId}`}
              role="button"
              tabIndex={0}
              focusable="true"
              aria-label={t('common.infoTooltip')}
            />
          }
          closeTooltipOnEscPress={true}
        >
          {titleInfo()}
        </ArrTooltipWrapper>
      </Box>
      <Box className="bar-body" data-testid="branding-and-redactions-body">
        <Box
          className="bar-body-toggle"
          data-testid="branding-and-redactions-toggle-row"
        >
          <ArrToggle
            checked={batchPrint?.includeRedactions}
            toggleSize="medium"
            onChange={(e) => {
              dispatch(setIsIncludeRedactions(e.target.checked));
            }}
            dataTestId="branding-redaction-toggle"
          />
          <Typography
            className="typo-text subhead5_size"
            tabIndex={0}
            data-testid={`branding-and-redactions-second-title-second-title-tooltip`}
          >
            {t('batchPrint.brandingAndRedactions.secondTitle')}
            <ArrTooltipWrapper
              title={t('batchPrint.brandingAndRedactions.secondTitle')}
              dataTestId="redactions-toggle-tooltip"
              trigger={
                <InfoIcon
                  fontSize="small"
                  className="info-icon"
                  data-testid={`add-user-title-info-${dataTestId}`}
                  role="button"
                  tabIndex={0}
                  focusable="true"
                  aria-label={t('common.infoTooltip')}
                />
              }
              closeTooltipOnEscPress={true}
            >
              {t('batchPrint.brandingAndRedactions.secondTitleTooltip')}
            </ArrTooltipWrapper>
          </Typography>
        </Box>
        {batchPrint?.includeRedactions ? (
          <Box
            className="bar-body-selector"
            data-testid="branding-and-redactions-selector-section"
          >
            <Typography
              data-testid={`branding-and-redactions-opaque-transparent`}
              className="typo-text body3_size selector-label"
              tabIndex={0}
            >
              {t('batchPrint.brandingAndRedactions.opaqueTransparent')}
            </Typography>
            <ArrSingleSelectDropdown
              dropdownLabel={''}
              dropdownItems={DropDownMenuOptions}
              selectedValues={dropdownSelection}
              onSelectionChange={handleDropdownChange}
              sizeVariant="medium"
              dataTestId={'asd-branding-access-control'}
              dropdownItemDisplay="text"
              dropdownItemKey="key"
              className="dropdown"
              tabIndex={-1}
              selectRestProps={{
                'aria-label': t(
                  'batchPrint.brandingAndRedactions.opaqueTransparent'
                ),
              }}
            />
          </Box>
        ) : null}
        <Divider
          data-testid="branding-and-redactions-divider"
          orientation="horizontal"
          flexItem
          className="bar-body-divider"
        />
        <Box
          className="bar-location"
          ref={locationSectionRef}
          data-testid="branding-location-section"
        >
          <Typography
            data-testid={`branding-and-redactions-branding-location`}
            className="typo-text subhead4_size bar-location-label"
            tabIndex={0}
          >
            {t('batchPrint.brandingAndRedactions.brandingLocation')}
            <ArrTooltipWrapper
              title={t('batchPrint.brandingAndRedactions.brandingLocation')}
              dataTestId="branding-location-section-tooltip"
              trigger={
                <InfoIcon
                  fontSize="small"
                  className="info-icon"
                  data-testid={`add-user-title-info-${dataTestId}`}
                  role="button"
                  tabIndex={0}
                  focusable="true"
                  aria-label={t('common.infoTooltip')}
                />
              }
              closeTooltipOnEscPress={true}
            >
              {brandingLocationInfo()}
            </ArrTooltipWrapper>
          </Typography>
          <ArrBrandingLocation
            label="Location"
            placeHolderName="Select a location"
            activeKey={activeKey}
            filledKeys={filledKeys}
            disabledKeys={disabledBrandingKeys}
            onPositionChange={handleLocationChange}
            onReset={handleReset}
            dataTestId="branding-location-selector"
          />
          {activeKey && (
            <Box
              className="branding-textarea-wrapper"
              data-testid="branding-textarea-wrapper"
            >
              <Box
                className="label-text"
                data-testid="branding-textarea-label-wrapper"
              >
                <Typography
                  className="typo-text subhead4_size"
                  data-testid={`branding-and-redactions-default-branding`}
                  tabIndex={0}
                  ref={defaultBrandingRef}
                >
                  {t('batchPrint.brandingAndRedactions.defaultBranding')}{' '}
                  <span
                    className="note"
                    data-testid={`branding-and-redactions-`}
                  >
                    (
                    {activeKey
                      ? activeKey
                          .split('-')
                          .map(
                            (word) =>
                              word.charAt(0).toUpperCase() + word.slice(1)
                          )
                          .join('-')
                      : ''}
                    )
                  </span>
                </Typography>
                <Typography
                  className="typo-text body3_size note"
                  data-testid={`branding-and-redactions-note`}
                  tabIndex={0}
                  ref={noteTextRef}
                >
                  {t('batchPrint.brandingAndRedactions.note')}
                </Typography>
              </Box>
              <ArrRichTextEditor
                key={activeKey}
                dataTestId={`branding-redaction-editor-${activeKey}`}
                value={
                  brandingPayload.find(
                    (item) => item.Location === LOCATION_MAP[activeKey]
                  )?.Html || ''
                }
                onChange={(value: string) => {
                  const strippedValue = value
                    .replace(/<[^>\r\n]{0,1000}>/g, '')
                    .trim();

                  const location = LOCATION_MAP[activeKey as BrandingKey];
                  const updated = [...brandingPayload];

                  const index = updated.findIndex(
                    (item) => item.Location === location
                  );

                  if (!strippedValue) {
                    if (index !== -1) {
                      updated.splice(index, 1);
                      dispatch(setBrandingDetails(updated));
                    }
                    return;
                  }

                  if (index !== -1) {
                    updated[index] = {
                      Location: location,
                      Html: value,
                    };
                  } else {
                    updated.push({
                      Location: location,
                      Html: value,
                    });
                  }

                  dispatch(setBrandingDetails(updated));
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default BrandingAndRedactions;
